// Trusted, code-authored article figures (hand-drawn SVG infographics).
//
// Why a registry instead of inline SVG in the markdown? The article markdown is
// fully HTML-escaped before rendering (see ui/markdown.js), so raw <svg> in a
// .md file would render as literal text. Figures are *trusted* visuals authored
// here in code, never user content, so they're safe to inject as innerHTML.
//
// An article references a figure with a line on its own:
//
//     [[figure:preterite-imperfect-timeline]]
//
// markdown.js turns that into <div data-figure="ID">, and article.js swaps in
// the SVG from this map after rendering. Each entry is a zero-arg function so a
// figure can be lazy/parameterised later without changing call sites.
//
// Accessibility rules followed by every figure (from the v2 pedagogy research):
//   • role="img" + <title>/<desc> so screen readers get a real description;
//   • colour is NEVER the only cue — shape, texture (dash), and text labels
//     each independently carry the pretérito-vs-imperfeito distinction;
//   • any motion sits behind `@media (prefers-reduced-motion: no-preference)`
//     so the default, reduced-motion render is fully static.

// ---- Pretérito vs Imperfeito timeline -----------------------------------
//
// The flagship A2 visual. It dramatises one sentence —
//   "Era de noite e chovia quando, de repente, o telefone tocou."
// — as a timeline: the imperfeito is a continuous striped ribbon (the film
// still rolling, no edges); the pretérito is a single solid pin dropped onto
// it (a bounded, framed photo) that interrupts the ongoing scene.
function preteriteImperfectTimeline() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 300" role="img"
         aria-labelledby="ptimp-title ptimp-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="ptimp-title">Pretérito versus imperfeito on a timeline</title>
      <desc id="ptimp-desc">A left-to-right timeline of the past. A long striped
        green ribbon labelled imperfeito runs across the whole timeline,
        standing for the ongoing background — it was night, it was raining, I
        was reading. A single solid amber pin labelled pretérito, tocou, drops
        onto the ribbon partway along, standing for the one bounded event that
        interrupts the scene: the phone rang.</desc>

      <style>
        .ptimp-axis { stroke: #9ca89c; stroke-width: 2; }
        .ptimp-tick-label { fill: #6b766b; font-size: 13px; font-family: Inter, system-ui, sans-serif; }
        .ptimp-ribbon { fill: #cfe0cf; stroke: #5f7a5f; stroke-width: 1.5; }
        .ptimp-ribbon-stripes { stroke: #5f7a5f; stroke-width: 1.5; opacity: 0.55; }
        .ptimp-label-imp { fill: #3f553f; font-size: 15px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .ptimp-sub-imp { fill: #5f7a5f; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .ptimp-pin { fill: #d9803a; stroke: #ffffff; stroke-width: 2.5; }
        .ptimp-pin-stem { stroke: #b9682c; stroke-width: 3; }
        .ptimp-label-pret { fill: #9a531f; font-size: 15px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .ptimp-sub-pret { fill: #b9682c; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .ptimp-quote { fill: #3f553f; font-size: 14px; font-style: italic; font-family: Inter, system-ui, sans-serif; }
        .ptimp-draw { stroke-dasharray: 560; stroke-dashoffset: 560; }
        @media (prefers-reduced-motion: no-preference) {
          .ptimp-draw { animation: ptimp-dash 1.1s ease-out forwards; }
          .ptimp-pin-pop { transform-box: fill-box; transform-origin: center bottom;
            animation: ptimp-pop 0.5s ease-out 0.9s both; }
        }
        @keyframes ptimp-dash { to { stroke-dashoffset: 0; } }
        @keyframes ptimp-pop { from { transform: scaleY(0); opacity: 0; } to { transform: scaleY(1); opacity: 1; } }
      </style>

      <!-- Scene caption -->
      <text x="30" y="34" class="ptimp-quote">“Era de noite e chovia quando, de repente, o telefone tocou.”</text>

      <!-- Imperfeito: a long striped ribbon = ongoing, edgeless background -->
      <g>
        <rect x="40" y="150" width="680" height="44" rx="22" class="ptimp-ribbon"/>
        <!-- diagonal stripes give the ribbon a texture cue independent of colour -->
        <g class="ptimp-ribbon-stripes">
          <line x1="70"  y1="152" x2="56"  y2="192"/>
          <line x1="130" y1="152" x2="116" y2="192"/>
          <line x1="190" y1="152" x2="176" y2="192"/>
          <line x1="250" y1="152" x2="236" y2="192"/>
          <line x1="430" y1="152" x2="416" y2="192"/>
          <line x1="490" y1="152" x2="476" y2="192"/>
          <line x1="550" y1="152" x2="536" y2="192"/>
          <line x1="610" y1="152" x2="596" y2="192"/>
          <line x1="670" y1="152" x2="656" y2="192"/>
        </g>
        <text x="56" y="138" class="ptimp-label-imp">imperfeito — the scene, still rolling</text>
        <text x="56" y="214" class="ptimp-sub-imp">era de noite · chovia · (eu) lia — no clear start or end</text>
      </g>

      <!-- Timeline axis with past → agora arrow -->
      <g>
        <line x1="40" y1="244" x2="724" y2="244" class="ptimp-axis ptimp-draw"/>
        <polygon points="724,244 712,239 712,249" fill="#9ca89c"/>
        <text x="40" y="266" class="ptimp-tick-label">passado</text>
        <text x="690" y="266" class="ptimp-tick-label">agora</text>
      </g>

      <!-- Pretérito: one solid pin dropped onto the ribbon = a bounded event -->
      <g class="ptimp-pin-pop">
        <line x1="380" y1="172" x2="380" y2="120" class="ptimp-pin-stem"/>
        <circle cx="380" cy="172" r="13" class="ptimp-pin"/>
        <text x="380" y="108" text-anchor="middle" class="ptimp-label-pret">pretérito</text>
        <text x="380" y="92"  text-anchor="middle" class="ptimp-sub-pret">tocou — one finished event</text>
      </g>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      The ribbon keeps rolling (imperfeito); the pin marks the single, finished moment that cuts in (pretérito).
    </figcaption>
  </figure>`;
}

// ---- Por vs Para: directional arrows -----------------------------------
//
// The classic confusion. The visual splits the field in two: PARA points
// forward to a goal/destination (a single arrow aimed at a target flag);
// POR loops back to a cause and threads through a path/exchange (a curved,
// dashed arrow that arcs through the middle). Direction (forward vs looping)
// and line texture (solid vs dashed) both carry the contrast independent of
// colour.
function porParaArrows() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 320" role="img"
         aria-labelledby="porpara-title porpara-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="porpara-title">Por versus para as two directions</title>
      <desc id="porpara-desc">Two contrasting arrows. On top, a straight solid
        amber arrow labelled para flies forward and lands on a goal flag,
        standing for purpose, destination and deadline — the aim ahead. Below,
        a curved dashed green arrow labelled por loops backward and threads
        through a path, standing for cause, motive, exchange and the route or
        means by which something happens.</desc>

      <style>
        .pp-axis { stroke: #d8ddd8; stroke-width: 1.5; }
        .pp-para-line { stroke: #d9803a; stroke-width: 4; fill: none; }
        .pp-para-head { fill: #d9803a; }
        .pp-por-line { stroke: #5f7a5f; stroke-width: 4; fill: none; stroke-dasharray: 9 7; }
        .pp-por-head { fill: #5f7a5f; }
        .pp-label-para { fill: #9a531f; font-size: 19px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .pp-label-por  { fill: #3f553f; font-size: 19px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .pp-sense-para { fill: #b9682c; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .pp-sense-por  { fill: #5f7a5f; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .pp-eg { font-size: 13px; font-style: italic; font-family: Inter, system-ui, sans-serif; }
        .pp-flag-pole { stroke: #9a531f; stroke-width: 3; }
        .pp-flag { fill: #f0c39a; stroke: #9a531f; stroke-width: 1.5; }
        .pp-draw { stroke-dashoffset: 520; stroke-dasharray: 520; }
        @media (prefers-reduced-motion: no-preference) {
          .pp-draw-para { animation: pp-dash 1s ease-out forwards; }
          .pp-draw-por  { animation: pp-dash 1s ease-out 0.35s forwards; }
        }
        @keyframes pp-dash { to { stroke-dashoffset: 0; } }
      </style>

      <line x1="40" y1="160" x2="720" y2="160" class="pp-axis"/>

      <!-- PARA: forward arrow to a goal flag -->
      <g>
        <text x="40" y="58" class="pp-label-para">para →</text>
        <text x="120" y="58" class="pp-sense-para">purpose · destination · deadline · recipient</text>
        <path d="M 60 96 L 612 96" class="pp-para-line pp-draw pp-draw-para"/>
        <polygon points="612,96 596,87 596,105" class="pp-para-head"/>
        <!-- goal flag = the aim ahead -->
        <line x1="640" y1="70" x2="640" y2="120" class="pp-flag-pole"/>
        <polygon points="640,72 686,82 640,92" class="pp-flag"/>
        <text x="60" y="128" class="pp-eg" fill="#9a531f">“Estudo para passar.” — I study in order to pass (the goal ahead).</text>
      </g>

      <!-- POR: curved dashed arrow looping back through a path -->
      <g>
        <text x="40" y="214" class="pp-label-por">↺ por</text>
        <text x="120" y="214" class="pp-sense-por">cause · motive · exchange · route / means · duration</text>
        <path d="M 612 250 C 470 300, 200 300, 70 252" class="pp-por-line pp-draw pp-draw-por"/>
        <polygon points="70,252 88,250 80,266" class="pp-por-head"/>
        <text x="60" y="300" class="pp-eg" fill="#3f553f">“Obrigado pela tua ajuda.” — because of / in exchange for your help (looking back).</text>
      </g>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      para aims forward at a goal (solid arrow → flag); por loops back to a cause or threads a path (dashed arc).
    </figcaption>
  </figure>`;
}

// ---- Ser vs Estar: essence vs state ------------------------------------
//
// Two columns. SER is a carved stone plinth (permanent identity, what a thing
// fundamentally IS); ESTAR is a weather vane / changing sky (a passing state
// or condition). The meaning-shift pairs (é aborrecido / está aborrecido) sit
// across the gap to show the same adjective swinging sense with the verb.
function serEstarEssenceState() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 340" role="img"
         aria-labelledby="serestar-title serestar-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="serestar-title">Ser versus estar — essence versus state</title>
      <desc id="serestar-desc">Two columns. On the left, ser sits on a solid
        carved stone plinth standing for permanent essence and identity — who
        or what something fundamentally is: origin, profession, defining
        traits. On the right, estar floats as a weather vane under a changing
        sky standing for a temporary state, condition, location or mood that
        can change. Between them, three adjective pairs show the same word
        shifting meaning: aborrecido is boring with ser but bored with estar;
        maduro is mature with ser but ripe with estar; rico is wealthy with ser
        but tasty with estar.</desc>

      <style>
        .se-col-label-ser { fill: #3f553f; font-size: 21px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .se-col-label-est { fill: #9a531f; font-size: 21px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .se-tag { font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .se-plinth { fill: #cfe0cf; stroke: #5f7a5f; stroke-width: 2; }
        .se-plinth-line { stroke: #5f7a5f; stroke-width: 1.5; opacity: 0.5; }
        .se-vane-pole { stroke: #b9682c; stroke-width: 3; }
        .se-vane { fill: #f0c39a; stroke: #9a531f; stroke-width: 1.5; }
        .se-cloud { fill: #fbf0e6; stroke: #d9803a; stroke-width: 1.5; }
        .se-pair-e { fill: #3f553f; font-size: 13px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .se-pair-est { fill: #9a531f; font-size: 13px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .se-pair-word { fill: #6b766b; font-size: 13px; font-style: italic; font-family: Inter, system-ui, sans-serif; }
        .se-divider { stroke: #e3e0d8; stroke-width: 1.5; stroke-dasharray: 4 5; }
        .se-vane-spin { transform-box: fill-box; transform-origin: bottom center; }
        @media (prefers-reduced-motion: no-preference) {
          .se-vane-spin { animation: se-sway 3.2s ease-in-out infinite; }
        }
        @keyframes se-sway { 0%,100% { transform: rotate(-7deg); } 50% { transform: rotate(7deg); } }
      </style>

      <!-- SER: stone plinth = fixed essence -->
      <g>
        <text x="40" y="40" class="se-col-label-ser">ser — essence</text>
        <text x="40" y="60" class="se-tag" fill="#5f7a5f">identity · origin · profession · defining trait</text>
        <rect x="70" y="120" width="150" height="120" rx="6" class="se-plinth"/>
        <line x1="70" y1="150" x2="220" y2="150" class="se-plinth-line"/>
        <line x1="70" y1="182" x2="220" y2="182" class="se-plinth-line"/>
        <line x1="70" y1="214" x2="220" y2="214" class="se-plinth-line"/>
        <text x="145" y="268" text-anchor="middle" class="se-tag" fill="#3f553f">“Sou da Noruega.” · “É médica.”</text>
        <text x="145" y="288" text-anchor="middle" class="se-tag" fill="#3f553f">carved in stone — what it is</text>
      </g>

      <!-- divider -->
      <line x1="380" y1="80" x2="380" y2="300" class="se-divider"/>

      <!-- ESTAR: weather vane under a changing sky = passing state -->
      <g>
        <text x="540" y="40" text-anchor="end" class="se-col-label-est">estar — state</text>
        <text x="720" y="60" text-anchor="end" class="se-tag" fill="#b9682c">condition · location · mood · result (changeable)</text>
        <path d="M 560 120 q -14 -22 14 -24 q 6 -20 32 -8 q 22 -10 30 12 q 22 2 8 22 z" class="se-cloud"/>
        <line x1="610" y1="150" x2="610" y2="240" class="se-vane-pole"/>
        <g class="se-vane-spin">
          <polygon points="610,150 660,166 610,182" class="se-vane"/>
        </g>
        <text x="610" y="268" text-anchor="middle" class="se-tag" fill="#9a531f">“Estou cansado.” · “Está em casa.”</text>
        <text x="610" y="288" text-anchor="middle" class="se-tag" fill="#9a531f">turns with the wind — how it is now</text>
      </g>

      <!-- meaning-shift pairs across the gap -->
      <g>
        <text x="380" y="120" text-anchor="middle" class="se-pair-word">aborrecido</text>
        <text x="300" y="120" text-anchor="end" class="se-pair-e">é = boring</text>
        <text x="460" y="120" class="se-pair-est">está = bored</text>
        <text x="380" y="160" text-anchor="middle" class="se-pair-word">maduro</text>
        <text x="300" y="160" text-anchor="end" class="se-pair-e">é = mature</text>
        <text x="460" y="160" class="se-pair-est">está = ripe</text>
        <text x="380" y="200" text-anchor="middle" class="se-pair-word">rico</text>
        <text x="300" y="200" text-anchor="end" class="se-pair-e">é = wealthy</text>
        <text x="460" y="200" class="se-pair-est">está = tasty</text>
      </g>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      ser is the carved plinth (essence — what something is); estar is the weather vane (a state that can change). The same adjective swings meaning between them.
    </figcaption>
  </figure>`;
}

// ---- Subjunctive trigger landscape -------------------------------------
//
// A decision-landscape for B2 mood choice. A central fork: every clause type
// faces one question — "real & known, or pending & unknown?" Solid-ground
// boxes (indicative: real/habitual/specific) sit on the left; misty,
// dashed-outline boxes (subjunctive: pending/hypothetical/unknown/negated)
// sit on the right. The "always subjunctive" connectors get their own banner
// so learners don't have to weigh them. Outline texture (solid vs dashed) and
// labels carry the split independent of colour.
function subjunctiveTriggerLandscape() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 420" role="img"
         aria-labelledby="subjland-title subjland-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="subjland-title">The subjunctive-trigger decision landscape</title>
      <desc id="subjland-desc">A decision map for choosing mood in B2 clauses.
        One central question — is the situation real and known, or pending and
        unknown? — splits every clause type two ways. On the left, solid-outline
        boxes take the indicative: temporal clauses about habits or the past,
        concessive clauses conceding a known fact, and relative clauses with a
        specific known antecedent. On the right, dashed-outline boxes take the
        subjunctive: temporal clauses pointing at the future, concessive even-if
        clauses, and relative clauses whose antecedent is unknown, sought or
        negated. A separate banner lists connectors that are always subjunctive:
        para que, sem que, antes que, a menos que, desde que, caso.</desc>

      <style>
        .sl-q { fill: #3f553f; font-size: 16px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .sl-col-ind { fill: #3f553f; font-size: 15px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .sl-col-sub { fill: #9a531f; font-size: 15px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .sl-box-ind { fill: #e7f0e7; stroke: #5f7a5f; stroke-width: 2; }
        .sl-box-sub { fill: #fbf0e6; stroke: #d9803a; stroke-width: 2; stroke-dasharray: 7 5; }
        .sl-box-title { font-size: 12.5px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .sl-box-eg { font-size: 11.5px; font-style: italic; font-family: Inter, system-ui, sans-serif; }
        .sl-fork { stroke: #9ca89c; stroke-width: 2; fill: none; }
        .sl-tag-ind { fill: #3f553f; font-size: 12px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .sl-tag-sub { fill: #9a531f; font-size: 12px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .sl-banner { fill: #cfe0cf; stroke: #5f7a5f; stroke-width: 1.5; }
        .sl-banner-text { fill: #3f553f; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .sl-banner-title { fill: #3f553f; font-size: 13px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
      </style>

      <!-- central question + fork -->
      <text x="380" y="30" text-anchor="middle" class="sl-q">Real &amp; known?  ·  or pending &amp; unknown?</text>
      <path d="M 380 42 L 380 64 M 380 64 C 380 80, 200 70, 200 92 M 380 64 C 380 80, 560 70, 560 92" class="sl-fork"/>
      <text x="150" y="84" class="sl-tag-ind">↙ real → indicative</text>
      <text x="470" y="84" class="sl-tag-sub">pending → subjunctive ↘</text>

      <!-- column headers -->
      <text x="40" y="112" class="sl-col-ind">INDICATIVE — solid ground</text>
      <text x="720" y="112" text-anchor="end" class="sl-col-sub">SUBJUNCTIVE — not yet real</text>

      <!-- temporal -->
      <g>
        <rect x="40" y="124" width="320" height="56" rx="10" class="sl-box-ind"/>
        <text x="54" y="146" class="sl-box-title" fill="#3f553f">Temporal · habit / past</text>
        <text x="54" y="166" class="sl-box-eg" fill="#5f7a5f">Quando chego a casa, janto.</text>
        <rect x="400" y="124" width="320" height="56" rx="10" class="sl-box-sub"/>
        <text x="414" y="146" class="sl-box-title" fill="#9a531f">Temporal · future / pending</text>
        <text x="414" y="166" class="sl-box-eg" fill="#b9682c">Quando chegar a casa, jantarei.</text>
      </g>

      <!-- concessive -->
      <g>
        <rect x="40" y="190" width="320" height="56" rx="10" class="sl-box-ind"/>
        <text x="54" y="212" class="sl-box-title" fill="#3f553f">Concessive · conceded fact</text>
        <text x="54" y="232" class="sl-box-eg" fill="#5f7a5f">Se bem que é caro, compro-o.</text>
        <rect x="400" y="190" width="320" height="56" rx="10" class="sl-box-sub"/>
        <text x="414" y="212" class="sl-box-title" fill="#9a531f">Concessive · even if</text>
        <text x="414" y="232" class="sl-box-eg" fill="#b9682c">Mesmo que chova, sairei.</text>
      </g>

      <!-- relative -->
      <g>
        <rect x="40" y="256" width="320" height="56" rx="10" class="sl-box-ind"/>
        <text x="54" y="278" class="sl-box-title" fill="#3f553f">Relative · known antecedent</text>
        <text x="54" y="298" class="sl-box-eg" fill="#5f7a5f">Tenho um apartamento que dá para o mar.</text>
        <rect x="400" y="256" width="320" height="56" rx="10" class="sl-box-sub"/>
        <text x="414" y="278" class="sl-box-title" fill="#9a531f">Relative · unknown / negated</text>
        <text x="414" y="298" class="sl-box-eg" fill="#b9682c">Procuro um apartamento que dê para o mar.</text>
      </g>

      <!-- always-subjunctive banner -->
      <g>
        <rect x="40" y="338" width="680" height="58" rx="12" class="sl-banner"/>
        <text x="60" y="362" class="sl-banner-title">Always subjunctive — no question needed:</text>
        <text x="60" y="384" class="sl-banner-text">para que · sem que · antes que · a menos que · desde que · caso</text>
      </g>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      One question splits every clause: real &amp; known → indicative (solid boxes); pending &amp; unknown → subjunctive (dashed boxes). A short list of connectors is always subjunctive.
    </figcaption>
  </figure>`;
}

// ---- Stem-changing verbs: where the vowel rises -------------------------
//
// The classic A2 pattern. A 2-column, 3-row present-tense grid for preferir
// (e → i). All six forms are drawn, and a thick outline is wrapped around the
// single form whose stem changes — eu prefiro — because in European Portuguese
// the raised stem vowel surfaces only in the first-person singular, where the
// stress lands on the stem. Every other person keeps the regular e stem. The
// change is carried three ways independent of colour: the highlight outline
// (shape), a solid ▲ marker + "stem changes" caption on the eu cell versus a
// hollow ○ + "regular stem" on the other five, and the changed letter set in
// bold.
function stemChangeBoot() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 400" role="img"
         aria-labelledby="boot-title boot-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="boot-title">Where the stem changes in the present tense</title>
      <desc id="boot-desc">A present-tense grid of preferir, an e-to-i
        stem-changing verb, in two columns and three rows. A thick outline
        highlights a single cell, eu prefiro, in the top left: only the eu form
        raises its stem vowel from e to i, marked with a solid triangle and the
        caption stem changes. Every other person — tu preferes, ele or ela
        prefere, nós preferimos, vós preferis, eles or elas preferem — keeps the
        regular e stem, marked with a hollow circle and the caption regular stem,
        because the stress falls on the ending rather than the stem.</desc>

      <style>
        .boot-head { fill: #3f553f; font-size: 19px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .boot-sub { fill: #5f7a5f; font-size: 13px; font-family: Inter, system-ui, sans-serif; }
        .boot-fill { fill: #e7f0e7; }
        .boot-outline { fill: none; stroke: #3f553f; stroke-width: 3.5; stroke-linejoin: round; }
        .boot-cell-out { fill: #fbf0e6; stroke: #d9803a; stroke-width: 2; stroke-dasharray: 6 5; }
        .boot-sep { stroke: #9ac09a; stroke-width: 1.25; opacity: 0.7; }
        .boot-pron { fill: #6b766b; font-size: 12px; font-family: Inter, system-ui, sans-serif; }
        .boot-form { fill: #2f3f2f; font-size: 21px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .boot-vowel { fill: #9a531f; font-weight: 800; }
        .boot-tag-chg { fill: #3f553f; font-size: 11px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .boot-tag-reg { fill: #9a531f; font-size: 11px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .boot-marker-chg { fill: #3f553f; }
        .boot-marker-reg { fill: none; stroke: #b9682c; stroke-width: 1.75; }
      </style>

      <text x="40" y="36" class="boot-head">preferir &nbsp;( e → i )</text>
      <text x="40" y="58" class="boot-sub">Only the eu form raises its stem vowel (e → i); every other person keeps the regular stem.</text>

      <!-- highlight fill behind the single changing cell: eu prefiro (top-left) -->
      <path d="M 140 90 L 380 90 L 380 160 L 140 160 Z" class="boot-fill"/>

      <!-- grid frame + separators (2 columns × 3 rows) -->
      <rect x="140" y="90" width="480" height="210" fill="none" class="boot-sep"/>
      <line x1="140" y1="160" x2="620" y2="160" class="boot-sep"/>
      <line x1="140" y1="230" x2="620" y2="230" class="boot-sep"/>
      <line x1="380" y1="90"  x2="380" y2="300" class="boot-sep"/>

      <!-- highlight outline drawn on top, around the single changing cell -->
      <path d="M 140 90 L 380 90 L 380 160 L 140 160 Z" class="boot-outline"/>

      <!-- c1r1 eu prefiro (CHANGES) -->
      <text x="260" y="118" text-anchor="middle" class="boot-pron">eu</text>
      <text x="260" y="142" text-anchor="middle" class="boot-form">pref<tspan class="boot-vowel">i</tspan>ro</text>
      <circle cx="168" cy="112" r="5" class="boot-marker-chg"/>
      <text x="260" y="156" text-anchor="middle" class="boot-tag-chg">▲ stem changes</text>

      <!-- c1r2 tu preferes (regular) -->
      <text x="260" y="188" text-anchor="middle" class="boot-pron">tu</text>
      <text x="260" y="212" text-anchor="middle" class="boot-form">pref<tspan style="fill:#5f7a5f">e</tspan>res</text>
      <circle cx="168" cy="182" r="5" class="boot-marker-reg"/>
      <text x="260" y="226" text-anchor="middle" class="boot-tag-reg">○ regular stem</text>

      <!-- c1r3 ele/ela/você prefere (regular) -->
      <text x="260" y="258" text-anchor="middle" class="boot-pron">ele / ela / você</text>
      <text x="260" y="282" text-anchor="middle" class="boot-form">pref<tspan style="fill:#5f7a5f">e</tspan>re</text>
      <circle cx="168" cy="252" r="5" class="boot-marker-reg"/>
      <text x="260" y="296" text-anchor="middle" class="boot-tag-reg">○ regular stem</text>

      <!-- c2r1 nós preferimos (regular) -->
      <text x="500" y="118" text-anchor="middle" class="boot-pron">nós</text>
      <text x="500" y="142" text-anchor="middle" class="boot-form">pref<tspan style="fill:#5f7a5f">e</tspan>rimos</text>
      <circle cx="408" cy="112" r="5" class="boot-marker-reg"/>
      <text x="500" y="156" text-anchor="middle" class="boot-tag-reg">○ regular stem</text>

      <!-- c2r2 vós preferis (regular) -->
      <text x="500" y="188" text-anchor="middle" class="boot-pron">vós</text>
      <text x="500" y="212" text-anchor="middle" class="boot-form">pref<tspan style="fill:#5f7a5f">e</tspan>ris</text>
      <circle cx="408" cy="182" r="5" class="boot-marker-reg"/>
      <text x="500" y="226" text-anchor="middle" class="boot-tag-reg">○ regular stem</text>

      <!-- c2r3 eles/elas/vocês preferem (regular) -->
      <text x="500" y="258" text-anchor="middle" class="boot-pron">eles / elas / vocês</text>
      <text x="500" y="282" text-anchor="middle" class="boot-form">pref<tspan style="fill:#5f7a5f">e</tspan>rem</text>
      <circle cx="408" cy="252" r="5" class="boot-marker-reg"/>
      <text x="500" y="296" text-anchor="middle" class="boot-tag-reg">○ regular stem</text>

      <!-- footer note on stress -->
      <text x="380" y="346" text-anchor="middle" class="boot-sub">The stem vowel rises only when the stress lands on it — in the eu form; on the ending elsewhere (so it stays e).</text>
      <text x="380" y="370" text-anchor="middle" class="boot-sub">Same idea for o → u (durmo, from dormir) and e → i (sirvo, from servir). The endings never change.</text>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      Only the eu form raises its stem vowel (e → i); every other person keeps the regular stem.
    </figcaption>
  </figure>`;
}

// ---- Gender by noun ending ----------------------------------------------
//
// An A1 reference grid that sorts the reliable noun endings into two panels —
// typically o (masculine) and typically a (feminine) — each ending shown as a
// chip with a worked example, then a strip of high-frequency exceptions to learn
// against the rule. Gender is encoded three ways independent of colour: the
// article word o vs a, a square ▪ marker for masculine versus a round ● marker
// for feminine, and the two-panel split itself; exceptions carry a dashed badge.
function genderEndingPatterns() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 400" role="img"
         aria-labelledby="gender-title gender-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="gender-title">Noun gender by ending</title>
      <desc id="gender-desc">Two panels sorting Portuguese noun endings by their
        usual gender. The left panel, typically masculine and taking o, lists the
        endings -o as in o livro, -or as in o amor, and -ema as in o problema.
        The right panel, typically feminine and taking a, lists -a as in a casa,
        -ção as in a canção, -são as in a decisão, -dade as in a cidade, and
        -gem as in a viagem. Masculine entries are marked with a square, feminine
        with a circle. A strip across the bottom flags common exceptions to learn
        against the rule: o dia, o mapa, a mão and a foto.</desc>

      <style>
        .gd-panel-m { fill: #3f553f; font-size: 17px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .gd-panel-f { fill: #9a531f; font-size: 17px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .gd-panel-sub { font-size: 12px; font-family: Inter, system-ui, sans-serif; }
        .gd-chip-m { fill: #e7f0e7; stroke: #5f7a5f; stroke-width: 1.75; }
        .gd-chip-f { fill: #fbf0e6; stroke: #d9803a; stroke-width: 1.75; }
        .gd-end-m { fill: #3f553f; font-size: 16px; font-weight: 800; font-family: Inter, system-ui, sans-serif; }
        .gd-end-f { fill: #9a531f; font-size: 16px; font-weight: 800; font-family: Inter, system-ui, sans-serif; }
        .gd-eg { fill: #4a554a; font-size: 14px; font-family: Inter, system-ui, sans-serif; }
        .gd-eg-end { font-weight: 800; }
        .gd-marker-m { fill: #5f7a5f; }
        .gd-marker-f { fill: #b9682c; }
        .gd-divider { stroke: #e3e0d8; stroke-width: 1.5; stroke-dasharray: 4 5; }
        .gd-exc-box { fill: #fbfaf6; stroke: #b9682c; stroke-width: 1.5; stroke-dasharray: 7 5; }
        .gd-exc-title { fill: #9a531f; font-size: 13px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .gd-exc-item { fill: #4a554a; font-size: 14px; font-family: Inter, system-ui, sans-serif; }
        .gd-exc-art { font-weight: 800; }
      </style>

      <!-- MASCULINE panel -->
      <text x="40" y="38" class="gd-panel-m">▪ o — typically masculine</text>
      <text x="40" y="58" class="gd-panel-sub" fill="#5f7a5f">endings that point to a masculine noun</text>

      <!-- -o -->
      <rect x="40" y="78" width="74" height="40" rx="9" class="gd-chip-m"/>
      <text x="77" y="104" text-anchor="middle" class="gd-end-m">-o</text>
      <text x="128" y="104" class="gd-eg">o livr<tspan class="gd-eg-end" fill="#3f553f">o</tspan></text>
      <!-- -or -->
      <rect x="40" y="128" width="74" height="40" rx="9" class="gd-chip-m"/>
      <text x="77" y="154" text-anchor="middle" class="gd-end-m">-or</text>
      <text x="128" y="154" class="gd-eg">o am<tspan class="gd-eg-end" fill="#3f553f">or</tspan></text>
      <!-- -ema -->
      <rect x="40" y="178" width="74" height="40" rx="9" class="gd-chip-m"/>
      <text x="77" y="204" text-anchor="middle" class="gd-end-m">-ema</text>
      <text x="128" y="204" class="gd-eg">o probl<tspan class="gd-eg-end" fill="#3f553f">ema</tspan> <tspan fill="#6b766b" font-size="11">(Greek -ema)</tspan></text>

      <!-- divider -->
      <line x1="380" y1="74" x2="380" y2="288" class="gd-divider"/>

      <!-- FEMININE panel -->
      <text x="720" y="38" text-anchor="end" class="gd-panel-f">a — typically feminine ●</text>
      <text x="720" y="58" text-anchor="end" class="gd-panel-sub" fill="#b9682c">endings that point to a feminine noun</text>

      <!-- -a -->
      <rect x="410" y="78" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="101" text-anchor="middle" class="gd-end-f">-a</text>
      <text x="500" y="101" class="gd-eg">a cas<tspan class="gd-eg-end" fill="#9a531f">a</tspan></text>
      <!-- -ção -->
      <rect x="410" y="120" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="143" text-anchor="middle" class="gd-end-f">-ção</text>
      <text x="500" y="143" class="gd-eg">a can<tspan class="gd-eg-end" fill="#9a531f">ção</tspan></text>
      <!-- -são -->
      <rect x="410" y="162" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="185" text-anchor="middle" class="gd-end-f">-são</text>
      <text x="500" y="185" class="gd-eg">a deci<tspan class="gd-eg-end" fill="#9a531f">são</tspan></text>
      <!-- -dade -->
      <rect x="410" y="204" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="227" text-anchor="middle" class="gd-end-f">-dade</text>
      <text x="500" y="227" class="gd-eg">a ci<tspan class="gd-eg-end" fill="#9a531f">dade</tspan></text>
      <!-- -gem -->
      <rect x="410" y="246" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="269" text-anchor="middle" class="gd-end-f">-gem</text>
      <text x="500" y="269" class="gd-eg">a via<tspan class="gd-eg-end" fill="#9a531f">gem</tspan></text>

      <!-- EXCEPTIONS strip -->
      <rect x="40" y="306" width="680" height="74" rx="12" class="gd-exc-box"/>
      <text x="60" y="332" class="gd-exc-title">Learn these against the rule:</text>
      <text x="60" y="360" class="gd-exc-item"><tspan class="gd-exc-art" fill="#3f553f">o</tspan> dia &nbsp;·&nbsp; <tspan class="gd-exc-art" fill="#3f553f">o</tspan> mapa &nbsp;·&nbsp; <tspan class="gd-exc-art" fill="#9a531f">a</tspan> mão &nbsp;·&nbsp; <tspan class="gd-exc-art" fill="#9a531f">a</tspan> foto</text>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      Endings give the gender most of the time — -o / -or / -ema lean o, -a / -ção / -são / -dade / -gem lean a — but a few common nouns break the pattern.
    </figcaption>
  </figure>`;
}

// ---- Forgetting curve vs spaced review ----------------------------------
//
// The flagship figure for the "How to Learn" page. Two memory-over-time curves:
// a dashed curve for a single exposure (decays fast to near-nothing), and a
// solid curve reviewed at widening intervals (each review resets retention high
// and the decay gets gentler). Colour is never the only cue — the single-look
// curve is dashed and labelled, the reviewed curve is solid with amber review
// pins, and text labels carry the meaning independently.
function forgettingCurve() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 320" role="img"
         aria-labelledby="fc-title fc-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="fc-title">The forgetting curve and how spaced review beats it</title>
      <desc id="fc-desc">A graph of memory strength over time. A dashed line shows
        a single study session: memory falls steeply and is almost gone within a
        week. A solid green line shows the same material reviewed at widening
        intervals — day one, two, four, a week, two weeks — where each amber
        review point lifts memory back near full and the line decays more slowly
        each time, staying high after a month.</desc>
      <style>
        .fc-axis { stroke: #9ca89c; stroke-width: 2; }
        .fc-grid { stroke: #e6ece6; stroke-width: 1; }
        .fc-label { fill: #6b766b; font-size: 13px; font-family: Inter, system-ui, sans-serif; }
        .fc-single { fill: none; stroke: #b9682c; stroke-width: 2.5; stroke-dasharray: 7 6; opacity: 0.85; }
        .fc-spaced { fill: none; stroke: #5f7a5f; stroke-width: 3; }
        .fc-pin { fill: #d9803a; stroke: #ffffff; stroke-width: 2; }
        .fc-single-label { fill: #9a531f; font-size: 13.5px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .fc-spaced-label { fill: #3f553f; font-size: 13.5px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .fc-axis-title { fill: #6b766b; font-size: 12px; font-family: Inter, system-ui, sans-serif; }
      </style>

      <!-- Axes -->
      <line class="fc-axis" x1="64" y1="40" x2="64" y2="262" />
      <line class="fc-axis" x1="64" y1="262" x2="724" y2="262" />
      <line class="fc-grid" x1="64" y1="60" x2="724" y2="60" />
      <text class="fc-axis-title" x="10" y="54" transform="rotate(-90 16 150)">Memory</text>

      <!-- Time ticks -->
      <text class="fc-label" x="64"  y="282" text-anchor="middle">Day 1</text>
      <text class="fc-label" x="180" y="282" text-anchor="middle">2</text>
      <text class="fc-label" x="320" y="282" text-anchor="middle">4</text>
      <text class="fc-label" x="470" y="282" text-anchor="middle">1 wk</text>
      <text class="fc-label" x="600" y="282" text-anchor="middle">2 wk</text>
      <text class="fc-label" x="708" y="282" text-anchor="middle">1 mo</text>

      <!-- Single exposure: steep decay to the floor -->
      <path class="fc-single" d="M64,60 C110,150 150,210 210,238 S360,258 724,258" />
      <text class="fc-single-label" x="250" y="232">One look — gone in days</text>

      <!-- Spaced review: sawtooth resets, each decay gentler -->
      <path class="fc-spaced" d="M64,60
        C100,120 140,150 180,150
        L180,66
        C230,108 280,132 320,134
        L320,62
        C390,96 430,112 470,114
        L470,60
        C540,82 570,92 600,94
        L600,58
        C650,70 690,76 724,80" />

      <!-- Review pins at each interval -->
      <circle class="fc-pin" cx="180" cy="66" r="5.5" />
      <circle class="fc-pin" cx="320" cy="62" r="5.5" />
      <circle class="fc-pin" cx="470" cy="60" r="5.5" />
      <circle class="fc-pin" cx="600" cy="58" r="5.5" />
      <text class="fc-spaced-label" x="486" y="44">Spaced review — it stays</text>
    </svg>
    <figcaption class="text-xs text-sage-500 mt-2 text-center">Each review is timed for just before you'd forget — that's when memory consolidates.</figcaption>
  </figure>`;
}

export const FIGURES = {
  "forgetting-curve": forgettingCurve,
  "preterite-imperfect-timeline": preteriteImperfectTimeline,
  "por-para-arrows": porParaArrows,
  "ser-estar-essence-state": serEstarEssenceState,
  "subjunctive-trigger-landscape": subjunctiveTriggerLandscape,
  "stem-change-boot": stemChangeBoot,
  "gender-ending-patterns": genderEndingPatterns,
};

// Inject any registered figures into a freshly-rendered article body. Unknown
// ids are left as an empty slot (no crash) so a typo can't blank the article.
export function hydrateFigures(root) {
  root.querySelectorAll("[data-figure]").forEach((slot) => {
    const fn = FIGURES[slot.dataset.figure];
    if (fn) slot.innerHTML = fn();
  });
}
